import axios from 'axios';

// Get API base URL from environment variable
// IMPORTANT: Vite exposes VITE_* vars at build time via import.meta.env.
// For production deployments (Cloudflare Pages), use the production API URL.
// For local development (including LAN IPs), use relative URLs (empty string)
// which work with the dev proxy.
//
// Strategy:
// 1. Use VITE_API_URL if explicitly set (allows override)
// 2. localhost / LAN / IP => relative URLs (dev proxy)
// 3. cognidaily.io => use proxy (empty baseURL) so requests are same-origin; Cloudflare
//    Pages Function proxies /api/* to api.cognidaily.io. Avoids CORS and ensures
//    Authorization header is sent. Set VITE_USE_API_PROXY=false to use direct API.
// 4. Other domains => use VITE_API_URL or empty
let API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

const isIpv4Host = (hostname: string) => /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
const isLanHost = (hostname: string) => {
  if (!isIpv4Host(hostname)) {
    return false;
  }
  const [a, b] = hostname.split('.').map(Number);
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
};

if (typeof window !== 'undefined') {
  const hostname = window.location.hostname;
  const isLocalhost =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';
  const isLocalNetwork = isLanHost(hostname) || isIpv4Host(hostname) || hostname.endsWith('.local');
  const isCognidailyDomain = hostname === 'cognidaily.io' || hostname === 'www.cognidaily.io';
  const useApiProxy = import.meta.env.VITE_USE_API_PROXY !== 'false';

  if (isLocalhost || isLocalNetwork) {
    API_BASE_URL = '';
  } else if (isCognidailyDomain) {
    API_BASE_URL = useApiProxy ? '' : 'https://api.cognidaily.io';
  } else if (!API_BASE_URL) {
    API_BASE_URL = 'https://api.cognidaily.io';
  }
} else if (!API_BASE_URL) {
  API_BASE_URL = 'https://api.cognidaily.io';
}

// Configure default axios instance (be defensive for unit tests that mock axios)
const ax: any = axios as any;
if (API_BASE_URL && ax?.defaults) {
  ax.defaults.baseURL = API_BASE_URL;
}

// Ensure credentials are sent with all requests (for cookies)
if (ax?.defaults) {
  ax.defaults.withCredentials = true;
}

// Create a public axios instance for unauthenticated requests
// This instance doesn't send Authorization headers
export const publicAxios: any = ax?.create
  ? ax.create({
      baseURL: API_BASE_URL,
      withCredentials: true, // Send cookies even for public requests (cookies are httpOnly, so safe)
    })
  : ax;

// Explicitly clear all default headers to ensure no Authorization header is sent
// (Some unit tests mock axios with minimal shapes; avoid crashing on import.)
if (publicAxios?.defaults?.headers) {
  publicAxios.defaults.headers.common = {};
  publicAxios.defaults.headers.get = {};
  publicAxios.defaults.headers.post = {};
  publicAxios.defaults.headers.put = {};
  publicAxios.defaults.headers.delete = {};
}

// Export the configured axios instance
export default axios;
